import { queryOptions, keepPreviousData, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { APIResponse, SortingDebtResponse } from './types';
import { identityApi } from '../../services/identity.api';

import { handlePolling, handleRetry, formatSortingDebts } from './utils';
import { useTracking } from '@common/hooks/useTracking';
import { useEffect } from 'react';

// API Call
const fetchDebtsData = async () => {
  const endpoint = '/v1/app/customer/debts';
  const { data } = await identityApi.get<APIResponse<SortingDebtResponse>>(endpoint);

  if (!data.data) {
    throw new Error('Failed to fetch sorting debts data');
  }

  return data.data;
};

// Query Factory
export const debtsQueries = {
  all: () =>
    queryOptions({
      queryKey: [{ scope: 'debts' }] as const,
      queryFn: fetchDebtsData,
      refetchInterval: handlePolling,
      retry: handleRetry,
      retryDelay: 1000,
      placeholderData: keepPreviousData,
    }),
};

// Custom Hook
export const useDebtsQuery = (options?: Partial<UseQueryOptions<SortingDebtResponse, Error, ReturnType<typeof formatSortingDebts>>>) => {
  const { debtLocated, debtSimulated } = useTracking();

  const { data } = useQuery({
    ...debtsQueries.all(),
  });

  useEffect(() => {
    if (!data?.partnerSystems || data?.partnerSystems.length === 0) {
      return;
    }

    const onlyPartnersWithDebt = data.partnerSystems.filter((x) => x.show && x.debts.length > 0);

    for (const partner of onlyPartnersWithDebt) {
      for (const debt of partner.debts) {
        const payment = debt?.paymentOptions?.[0];
        debtLocated(partner);
        debtSimulated(payment, debt, partner);
      }
    }
  }, [data]);

  return useQuery({
    ...debtsQueries.all(),
    select: formatSortingDebts,
    ...options,
  });
};
